import React, { useState } from "react";
import styles from "./learningToolCloze.module.css";
import LearningToolButtons from "../learningToolButtons/LearningToolButtons";
import LearningUnitButtons from "../learningUnitButtons/LearningUnitButtons";
import { validateString } from "../../utilities/utils";
import LearningToolModal from "../learningToolModal/LearningToolModal";
import ClozeModal from "./clozeModal/ClozeModal";
import axios from "axios";

const apiUrl = process.env.REACT_APP_URL;

const LearningToolCloze = ({
  tool,
  mayEditTool,
  removeToolFromList,
  maxUnitsPerTool,
}) => {
  /* The LearningToolCloze component is responsible for displaying, adding, editing, removing, and checking answers of a Cloze Learning Tool. Each tool may contain one or more learning units. A cloze learning unit is an exercise in which the user should complete a gap word in a sentence. */

  const [toolUnits, setToolUnits] = useState(tool.units);
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [activeModalUnit, setActiveModalUnit] = useState(null);
  const [isToolModalOpen, setIsToolModalOpen] = useState(false);
  const [isUnitContentModalOpen, setIsUnitContentModalOpen] = useState(false);
  const [isNewUnitModalOpen, setIsNewUnitModalOpen] = useState(false);
  const [activeContentModalUnit, setActiveContentModalUnit] = useState(null);
  const [activeUnitIndex, setActiveUnitIndex] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [unitsFeedback, setUnitsFeedback] = useState({});

  const learningToolId = tool.learning_tool_id;

  /* Show the modal to add a new unit to the tool */
  const onAddUnit = () => {
    setIsNewUnitModalOpen(true);
  };

  /**
   * /* Show the confirmation modal to confirm deletion of a unit in the tool
   * @param {*} unitId - the id of the unit to be deleted
   */
  const onDeleteUnit = (unitId) => {
    setActiveModalUnit(unitId);
    setIsUnitModalOpen(true);
  };

  /**
   * Show the modal for confirmation of deleting the tool.
   */
  const onDeleteTool = () => {
    setIsToolModalOpen(true);
  };

  // Close the unit modal
  const closeUnitModal = () => {
    setIsUnitModalOpen(false);
  };

  // Close the tool confirmation modal
  const closeToolModal = () => {
    setIsToolModalOpen(false);
  };

  // Close the unit content modal
  const closeUnitContentModal = () => {
    setIsUnitContentModalOpen(false);
    setIsNewUnitModalOpen(false);
  };

  /* Delete a unit from the tool */
  const handleModalConfirmUnitDelete = async () => {
    try {
      closeUnitModal();

      const res = await axios.put(
        `${apiUrl}/learning_units/deactivate/${learningToolId}/${activeModalUnit}`,
        {},
        {
          withCredentials: true,
        }
      );

      if (res.status === 200 && res.data.success) {
        if (toolUnits.length <= 1) {
          closeUnitModal();
          removeToolFromList(learningToolId);
        } else {
          setToolUnits((prevUnits) =>
            prevUnits.filter(
              (unit) => unit.learning_unit_id !== activeModalUnit
            )
          );
          closeUnitModal();
        }
      } else {
        console.error("Error - unit was not deleted.");
      }
    } catch (error) {
      console.error("Error - unit was not deleted.");
    }
  };

  /* Delete the tool */
  const handleModalConfirmToolDelete = async () => {
    try {
      const res = await axios.put(
        `${apiUrl}/learning_tools/deactivate/${learningToolId}`,
        {},
        {
          withCredentials: true,
        }
      );

      if (res.status === 200 && res.data.success) {
        removeToolFromList(learningToolId); // Remove the tool from the list
      } else {
        console.error("Error - tool was not deleted.");
      }
    } catch (error) {
      console.error("Error - tool was not deleted.");
    }

    closeToolModal();
  };

  /**
   * Handle text input change, when the user tries to solve a cloze unit. Validate the value and if valid - update the user's answers of the whole tool with the updated answer of the unit.
   * @param {*} unitId - the id of the unit to update the answer of
   */
  const handleInputChange = (e, unitId) => {
    const userInput = validateString(e.target.value, "Cloze input value");
    setUserAnswers({
      ...userAnswers,
      [unitId]: userInput,
    });
  };

  /**
   * Check a user's answer of a cloze unit, and update the feedback accordingly.
   * @param {*} unitId - the id of the unit to check the answer of
   */
  const handleCheckAnswer = (unitId) => {
    const unitToCheck = toolUnits.find(
      (unit) => unit.learning_unit_id === unitId
    );

    if (!unitToCheck) {
      console.error(`Unit with ID ${unitId} not found.`);
      return;
    }

    const userAnswer = userAnswers[unitId];
    const correctAnswer = getUnitCorrectAnswer(unitId);

    const feedbackClass = userAnswer === correctAnswer ? "✔" : "✘";
    const feedbackType = userAnswer === correctAnswer ? "correct" : "incorrect";

    setUnitsFeedback({
      ...unitsFeedback,
      [unitId]: { symbol: feedbackClass, type: feedbackType },
    });
  };

  /**
   * Check the user's answers to all the tool units, and update the feedback accordingly.
   */
  const handleCheckAllAnswers = () => {
    const updatedFeedback = {};

    toolUnits.forEach((unit) => {
      const unitId = unit.learning_unit_id;
      const userAnswer = userAnswers[unitId] || "";
      const correctAnswer = getUnitCorrectAnswer(unitId);

      updatedFeedback[unitId] = {
        symbol: userAnswer === correctAnswer ? "✔" : "✘",
        type: userAnswer === correctAnswer ? "correct" : "incorrect",
      };
    });

    setUnitsFeedback(updatedFeedback);
  };

  /**
   * Get the correct answer of a unit.
   * @param {*} unitId - the id of the unit to get the correct answer of.
   * @returns string - the correct answer of the requested unit.
   */
  function getUnitCorrectAnswer(unitId) {
    const reqUnit = toolUnits.filter(
      (unit) => unit.learning_unit_id === unitId
    );

    return reqUnit[0].unitSolutions[0].solution_text;
  }

  /**
   * Replace a word in a given string with a text input field.
   * @param {*} str - the string to manipulate
   * @param {*} wordNumber - the (1-based) index of the gap word
   * @param {*} unitId - the id of the unit to update the input field in
   */
  function replaceWordWithInput(str, wordNumber, unitId) {
    const words = str.split(" ");
    if (wordNumber < 1 || wordNumber > words.length) {
      return str;
    }

    const punctuationMatch = words[wordNumber - 1].match(/[\W_]+$/);
    const punctuation = punctuationMatch ? punctuationMatch[0] : "";

    words[wordNumber - 1] = (
      <>
        <input
          className={styles.gapInput}
          type="text"
          value={userAnswers[unitId] || ""}
          onChange={(e) => handleInputChange(e, unitId)}
        />
        {punctuation}
      </>
    );

    return (
      <span>
        {words.map((word, index) => (
          <span key={index}>
            {index > 0 && " "}
            {word}
          </span>
        ))}
      </span>
    );
  }

  /**
   * Handle click on the edit unit button - opens the modal with the current unit data. The user will be able to edit it.
   * @param {*} unitId - the id of the unit
   * @param {*} idx - the unit current index within the tool.
   */
  const handleEditUnit = (unitId, idx) => {
    setActiveContentModalUnit(unitId);
    setActiveUnitIndex(idx);
    setIsUnitContentModalOpen(true);
  };

  /**
   * Update the display of a unit within the tool.
   * @param {*} updatedUnitContent - an object with the updated content of the unit
   */
  const handleUnitContentConfirm = (updatedUnitContent) => {
    setToolUnits((prevUnits) =>
      prevUnits.map((unit) =>
        unit.learning_unit_id === activeContentModalUnit
          ? { ...unit, ...updatedUnitContent }
          : unit
      )
    );

    closeUnitContentModal();
  };

  /**
   * Add a new unit to the tool display
   * @param {*} newUnit - an object with the new unit data, inside a newUnit property.
   */
  const addNewUnitToList = (newUnit) => {
    setToolUnits([...toolUnits, newUnit.newUnit]);
    setIsNewUnitModalOpen(false);
  };

  /**
   * Prepare the tool display, by getting the original units data, and replacing gap words with input fields.
   */
  const toolData = toolUnits.map((unit, idx) => {
    const textWithGap = replaceWordWithInput(
      unit.learning_unit_text,
      unit.completion_word_num,
      unit.learning_unit_id
    );

    return (
      <div key={idx} className={styles.singleUnit}>
        <div className={styles.questionContainer}>
          <p>
            {idx + 1}. {textWithGap}{" "}
          </p>
          <p
            className={`${styles.unitFeedback} ${
              styles[unitsFeedback[unit.learning_unit_id]?.type]
            }`}
          >
            {unitsFeedback[unit.learning_unit_id]?.symbol}
          </p>
          <LearningUnitButtons
            mayEditTool={mayEditTool}
            isVoc={false}
            unitId={unit.learning_unit_id}
            onCheckAnswer={handleCheckAnswer}
            onEditUnit={() => handleEditUnit(unit.learning_unit_id, idx)}
            onDeleteUnit={onDeleteUnit}
          />
        </div>
      </div>
    );
  });

  return (
    <div className={styles.clozeTool}>
      <div className={styles.toolButtons}>
        {mayEditTool && (
          <LearningToolButtons
            toolId={learningToolId}
            handleAddNewUnit={onAddUnit}
            onDeleteTool={onDeleteTool}
            unitsNum={toolUnits.length}
            maxUnitsPerTool={maxUnitsPerTool}
          />
        )}
      </div>
      <h2>Cloze</h2>
      <p className={styles.toolCreator}>By {tool.username}</p>
      <p className={styles.instructions}>Fill in the gaps:</p>
      <div className={styles.toolData}>{toolData}</div>
      <div className={styles.checkBtnContainer}>
        <button onClick={handleCheckAllAnswers}>Check All Answers</button>
      </div>
      <LearningToolModal
        message="Are you sure you want to delete this learning unit?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isUnitModalOpen}
        onClose={closeUnitModal}
        onConfirm={handleModalConfirmUnitDelete}
      />

      <LearningToolModal
        message="Are you sure you want to delete this learning tool?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isToolModalOpen}
        onClose={closeToolModal}
        onConfirm={handleModalConfirmToolDelete}
      />

      <ClozeModal
        header={`Edit Cloze Unit ${activeUnitIndex + 1}`}
        unitContent={toolUnits.find(
          (unit) => unit.learning_unit_id === activeContentModalUnit
        )}
        approveBtnText="Update"
        cancelBtnText="Cancel"
        show={isUnitContentModalOpen}
        onClose={closeUnitContentModal}
        onConfirm={handleUnitContentConfirm}
        learningToolId={learningToolId}
      />

      <ClozeModal
        header={`Add Cloze Unit`}
        unitContent={null}
        approveBtnText="Create"
        cancelBtnText="Cancel"
        show={isNewUnitModalOpen}
        onClose={closeUnitContentModal}
        onConfirm={addNewUnitToList}
        learningToolId={learningToolId}
      />
    </div>
  );
};

export default LearningToolCloze;
